import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { debounceTime, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ConfirmareDocumenteService } from './confirmare-documente.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { helper } from 'src/environments/helper';
import { FormControl, FormGroup } from '@angular/forms';
import { AppFilesService } from 'src/app/services/app-files.service';
import { MatCalendar } from '@angular/material/datepicker';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
    selector: 'app-confirmare-documente',
    templateUrl: './confirmare-documente.component.html',
    styleUrls: ['./confirmare-documente.component.scss']
})
export class ConfirmareDocumenteComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    @ViewChild(PopoverDirective) popover!: PopoverDirective;
    helper = helper;

    isLoading = false;
    showCard = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    isUsersLoading = false;
    dropdownControl = new FormControl();
    users: any[] = [];
    showMessage = true;
    loadComplete = false;

    isCnpLoading = false;
    dropdownControlCnp = new FormControl();
    cnps: any[] = [];
    showMessageCnp = true;
    loadCompleteCnp = false;

    // Popover dates

    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    tomorrow: Date = new Date();
    selectedStartDate: Date = new Date(new Date().getFullYear(), 0, 1);
    private _selectedEndDate: Date = new Date();
    selectedFilteringButton: string = 'current_year';

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);
        this.selectedStartDate = date;
        this.updateCalendarView(this.startCalendar, date);
        this.rerenderDT();
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.rerenderDT();
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date

    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    dateFilteringOptions: { id: number; value: string }[] = [
        { id: 1, value: 'Adăugare' },
        { id: 2, value: 'Email' },
        { id: 3, value: 'Sms' },
        { id: 4, value: 'Prima accesare' },
        { id: 5, value: 'Ultima accesare' },
    ];

    dateFilterOption: number = 1;

    scopParameters: { key: string; value: number }[] = [
        { key: '-- Toate --', value: -1 },
        { key: 'Cu specificație', value: 0 },
        { key: 'Vânzare', value: 1 },
        { key: 'Succesiune', value: 2 },
    ];

    parameters: { key: string; value: number }[] = [
        { key: '-- Toate --', value: -1 },
        { key: 'Da', value: 1 },
        { key: 'Nu', value: 0 },
    ];

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);
        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    searchParamsForm!: FormGroup;

    //Form Initial Values

    initialFormValues = {
        id_cetatean: '',
        cnp: '',
        email: '',
        telefon: ''
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            id_cetatean: new FormControl(),
            cnp: new FormControl(),
            email: new FormControl(),
            telefon: new FormControl(),
        });
    }

    dtLink = environment.interop.basePath + environment.interop.platform.nomenclatoare.documenteCetatean.confirmationDT;

    constructor(
        private http: HttpClient,
        private confirmareDocumenteService: ConfirmareDocumenteService,
        private appFilesService: AppFilesService,
        private permissionsService: PermissionsService
    ) {
        this.dropdownControl.valueChanges
            .pipe(debounceTime(600))
            .subscribe(user => {
                this.fetchUsers(user);
                this.showMessage = true;
                this.loadComplete = false;
            });

        this.dropdownControlCnp.valueChanges
            .pipe(debounceTime(600))
            .subscribe(cnp => {
                this.fetchCnps(cnp);
                this.showMessageCnp = true;
                this.loadCompleteCnp = false;
            });
    }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    fetchUsers(user: string) {
        if (user.length >= 2) {
            this.isUsersLoading = true;
            this.confirmareDocumenteService.getUsers(user).subscribe({
                next: (response: any) => {
                    this.users = response.data;
                    this.isUsersLoading = false;
                    this.loadComplete = true;
                    this.showMessage = this.users.length === 0;
                },
                error: (err: any) => {
                    this.isUsersLoading = false;
                    this.showMessage = true;
                    this.loadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.users = [];
            this.showMessage = true;
            this.loadComplete = false;
        }
    }

    fetchCnps(cnp: string) {
        if (cnp.length >= 2) {
            this.isCnpLoading = true;
            this.confirmareDocumenteService.getCnp(cnp).subscribe({
                next: (response: any) => {
                    this.cnps = response.data;
                    this.isCnpLoading = false;
                    this.loadCompleteCnp = true;
                    this.showMessageCnp = this.cnps.length === 0;
                },
                error: (err: any) => {
                    this.isCnpLoading = false;
                    this.showMessageCnp = true;
                    this.loadCompleteCnp = true;
                    console.log(err);
                }
            });
        } else {
            this.cnps = [];
            this.showMessageCnp = true;
            this.loadCompleteCnp = false;
        }
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            order: [[0, 'desc']],
            serverSide: true,
            searching: false,
            responsive: true,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.filters = {
                    ...this.searchParamsForm.value,
                    date_type: +this.dateFilterOption
                };
                dataTablesParameters.date_from = this.formatDate(
                    this.selectedStartDate
                );
                dataTablesParameters.date_to = this.formatDate(this.selectedEndDate);

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id_log',
                },
                {
                    title: 'Cetățean/Companie',
                    data: null,
                    orderable: false,
                    render: (row: any) => {
                        let cnp_cif;

                        if (row.f_nume === '') {
                            cnp_cif = 'CNP';
                        } else {
                            cnp_cif = 'CIF';
                        }

                        return `
                            <div class="d-flex flex-column">
                                ` + (row.f_nume !== '' ? `<span class="d-flex align-items-center gap-1"><img src="/assets/icons/companii.svg" alt="Image" style="width: 20px; height: 20px;">${row.f_nume}</span>` : '') + `
                                <span class="d-flex align-items-center gap-1"><img src="/assets/icons/icon-user.svg" alt="Image" style="width: 20px; height: 20px;">${row.citizen_name}</span>
                                <span>${cnp_cif}: ${row.cnp_cif}</span>
                                ` + (row.sms_number !== null ? `<span class="d-flex align-items-center gap-1"><i class="fa-solid fa-phone"></i> ${row.sms_number}</span>` : '') + `
                                ` + (row.email !== null ? `<span class="d-flex align-items-center gap-1"><i class="fa-regular fa-envelope"></i> ${row.email}</span>` : '') + `
                            </div>
                        `;
                    }
                },
                {
                    title: 'Info Document',
                    data: 'file',
                    orderable: false,
                    render: (data: any) => {
                        const icon = '<i class="fa-regular fa-file"></i>';
                        return `${icon} ${data.orig_name}`
                    }
                },
                {
                    title: 'Status',
                    data: null,
                    orderable: false,
                    render: (row: any) => {
                        return `
                            ` + (row.first_download !== null ?
                                `
                                <span class="label label-success">Confirmat</span><br/>
                                <span>Confirmat la: <strong>${this.formatRomanianDate(row.first_download)}</strong></span><br/>
                                <span>Ultima descărcare: <strong>${this.formatRomanianDate(row.last_download)}</strong></span><br/>
                                <span>Nr. accesări: <strong>${row.clicked}</strong></span>
                                ` : `
                                <span class="label label-danger">Neconfimat</span>
                                `) + `
                        `
                    }
                },
                {
                    title: 'Document generat',
                    data: 'created_at',
                    render: (data: any) => {
                        return this.formatRomanianDate(data);
                    }
                },
                {
                    title: 'Ultima actualizare',
                    data: 'updated_at',
                    render: (data: any) => {
                        return this.formatRomanianDate(data);
                    }
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: () => {
                        return `
                            <div class="d-flex justify-content-left align-items-center">
                                <button style="color: #fff;" class="action-btn signatures ms-1" title="Descarcă fișier">
                                    DESCARCĂ
                                </button>
                                <button style="color: white;" class="action-btn edit ms-1 d-flex gap-1" title="Descarcă confirmare sms">
                                    <i class="fa-solid fa-cloud-arrow-down" style="color: #fff;"></i>Confirmare SMS
                                </button>
                                <button style="color: white;" class="action-btn delete ms-1 d-flex gap-1" title="Descarcă confirmare email">
                                   <i class="fa-solid fa-cloud-arrow-down" style="color: #fff;"></i>Confirmare Email
                                </button>
                            </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const downloadButton = lastTd?.querySelector('.signatures') as HTMLElement;
                const confSmsButton = lastTd?.querySelector('.edit') as HTMLElement;
                const confEmailButton = lastTd?.querySelector('.delete') as HTMLElement;

                if (downloadButton) {
                    if (!this.permissionsService.hasPermission('auth-module.solicitari.confirmare-documents.download')) {
                        downloadButton.style.display = 'none';
                    }

                    downloadButton.addEventListener('click', () => {
                        this.downloadDocument(data.id_document, data.id_fisier);
                    });
                }

                if (confSmsButton) {
                    if (!this.permissionsService.hasPermission('auth-module.solicitari.confirmare-documents.download-confirmare')) {
                        confSmsButton.style.display = 'none';
                    }

                    if (data.sms_confirmation_file_path === null) {
                        confSmsButton.style.display = 'none';
                    }

                    confSmsButton.addEventListener('click', () => {
                        this.downloadSmsEmail(data.id_log, 2);
                    });
                }


                if (confEmailButton) {
                    if (!this.permissionsService.hasPermission('auth-module.solicitari.confirmare-documents.download-confirmare')) {
                        confEmailButton.style.display = 'none';
                    }

                    if (data.email_confirmation_path === null) {
                        confEmailButton.style.display = 'none';
                    }

                    confEmailButton.addEventListener('click', () => {
                        this.downloadSmsEmail(data.id_log, 1);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });

        this.showCard = false;
    }

    downloadDocument(idDocument: number, idFisier: number) {
        let fullPath = this.confirmareDocumenteService.fileDownloadUrl + idDocument + '/' + idFisier;
        this.isLoading = true;
        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('File download error: ', err);
                this.isLoading = false;
            })
    }

    downloadSmsEmail(idDocument: number, type: number) {
        let fullPath = this.confirmareDocumenteService.smsEmailDownloadUrl + idDocument + '/' + type;
        this.isLoading = true;
        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('File download error: ', err);
                this.isLoading = false;
            })
    }

    formatDate(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    onDateFilterChange(): void {
        this.rerenderDT();
    }

    formatRomanianDate(dateTimeString: string): string {
        const [datePart, timePart] = dateTimeString.split(' ');
        const [year, month, day] = datePart.split('-');
        return `${day}.${month}.${year} ${timePart}`;
    }
}
